import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {MethodExecutionConditionLimitError} from 'studies/typed/shared/procedure-runs/execution-status/MethodExecutionLimitError';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import ControlledNumericInput from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {NumericInputChangeEvent} from 'util/Types';

export interface PlannedConditionsSpec {
  numConditions: number;
  numReplicates: number;
}

interface PlannedConditionsState extends PlannedConditionsSpec {
  validationError?: JSX.Element;
}

export interface AddPlannedConditionsProps {
  onRequestClose: () => void;
  onRequestSubmit: (result: PlannedConditionsSpec) => void;
  remainingConditions: number;
  procedureType: ProcedureType;
}

export default function AddPlannedConditionsModal({
  onRequestClose,
  onRequestSubmit,
  remainingConditions,
  procedureType,
}: AddPlannedConditionsProps): JSX.Element {
  const [conditionsState, setConditionsState] = React.useState<PlannedConditionsState>(
    validateConditionsSpec({numConditions: 1, numReplicates: 1}, procedureType)
  );

  function validateConditionsSpec(
    spec: PlannedConditionsSpec,
    procedureType: ProcedureType
  ): PlannedConditionsState {
    if (spec.numConditions < 1 || spec.numReplicates < 1) {
      return {
        ...spec,
        validationError: (
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`Number of ${getProcedureRunConditionTextForProcedureType(procedureType, {
              plural: true,
            })} and number of replicates must be at least 1`}
          />
        ),
      };
    }
    if (spec.numReplicates > EXPERIMENT_VALIDATION_CONSTANTS.MAX_REPLICATES_PER_CONDITION) {
      return {
        ...spec,
        validationError: (
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`Number of replicates per condition cannot exceed ${EXPERIMENT_VALIDATION_CONSTANTS.MAX_REPLICATES_PER_CONDITION}`}
          />
        ),
      };
    }
    if (spec.numConditions > remainingConditions) {
      return {
        ...spec,
        validationError: <MethodExecutionConditionLimitError />,
      };
    }
    return spec;
  }

  return (
    <Modal
      buttons={[
        {
          text: `Add ${conditionsState.numConditions} ${pluralize(
            conditionsState.numConditions,
            getProcedureRunConditionTextForProcedureType(procedureType)
          )}`,
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: conditionsState.validationError != null,
          dataTestAttrs: {component: 'AddPlannedConditionsModal', element: 'addPlannedConditionsButton'},
        },
      ]}
      title={`Add multiple ${getProcedureRunConditionTextForProcedureType(procedureType, {plural: true})}`}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit({
          numConditions: conditionsState.numConditions,
          numReplicates: conditionsState.numReplicates,
        });
      }}
    >
      <div className={css(styles.container)}>
        <FormGroup>
          <FieldLabel
            label={`Number of ${getProcedureRunConditionTextForProcedureType(procedureType, {plural: true})}`}
          />
          <ControlledNumericInput
            value={conditionsState.numConditions}
            onChange={(e: NumericInputChangeEvent) => {
              setConditionsState(
                validateConditionsSpec(
                  {numConditions: e.target.numericValue ?? 0, numReplicates: conditionsState.numReplicates},
                  procedureType
                )
              );
            }}
            validateInteger={true}
            required={true}
            min={1}
            focusOnMount={true}
            dataTestAttrs={{component: 'AddConditionsModal', element: 'numConditionsInput'}}
          />
        </FormGroup>
        <FormGroup>
          <FieldLabel
            label={`Number of replicates per ${getProcedureRunConditionTextForProcedureType(procedureType)}`}
          />
          <ControlledNumericInput
            value={conditionsState.numReplicates}
            onChange={(e: NumericInputChangeEvent) => {
              setConditionsState(
                validateConditionsSpec(
                  {numConditions: conditionsState.numConditions, numReplicates: e.target.numericValue ?? 0},
                  procedureType
                )
              );
            }}
            validateInteger={true}
            required={true}
            min={1}
            dataTestAttrs={{component: 'AddPlannedConditionsModal', element: 'numReplicatesInput'}}
          />
        </FormGroup>
        {conditionsState.validationError && (
          <FormGroup disableBottomMargin={true}>{conditionsState.validationError}</FormGroup>
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    // The designs call for a 24px total padding. The modal already includes 16px of padding.
    padding: 8,
  },
});
